'use client';

import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Skeleton } from '@/components/ui/skeleton.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';

interface SearchFiltersFooterProps {
  hasResults: boolean;
  isLoading: boolean;
  isError: boolean;
  isDraftValid?: boolean;
  onRetry: () => void;
  onClear: () => void;
  onApply: () => void;
}

interface ApplyButtonState {
  label: string;
  onClick: () => void;
  disabled: boolean;
}

interface GetApplyButtonStateParams {
  hasResults: boolean;
  isError: boolean;
  isDraftValid: boolean;
  onRetry: () => void;
  onApply: () => void;
}

function getApplyButtonState({
  hasResults,
  isError,
  isDraftValid,
  onRetry,
  onApply,
}: GetApplyButtonStateParams): ApplyButtonState {
  if (isError) {
    return { label: 'Tentar novamente', onClick: onRetry, disabled: false };
  }

  if (!isDraftValid) {
    return { label: 'Ver resultados', onClick: onApply, disabled: true };
  }

  if (!hasResults) {
    return { label: 'Nenhum resultado', onClick: onApply, disabled: true };
  }

  return { label: 'Ver resultados', onClick: onApply, disabled: false };
}

export function SearchFiltersFooter({
  hasResults,
  isLoading,
  isError,
  isDraftValid = true,
  onRetry,
  onClear,
  onApply,
}: SearchFiltersFooterProps) {
  const applyButton = getApplyButtonState({ hasResults, isError, isDraftValid, onRetry, onApply });

  return (
    <div className="flex shrink-0 flex-col gap-3 bg-white px-3 py-2.5 shadow-modal">
      {isError && (
        <div
          role="alert"
          className="flex items-center gap-3 rounded-2xl border border-warning-300 bg-warning-100 px-4 py-3 shadow-floating"
        >
          <Icon icon={faCircleExclamation} className="text-xl text-warning-600" aria-hidden />
          <Typography as="span" variant={TypographyVariant.BODY_TEXT} className="font-semibold text-warning-800">
            Não foi possível atualizar os resultados.
          </Typography>
        </div>
      )}

      <div
        className="flex items-center gap-2 rounded-xl border border-gray-300 bg-gray-100 p-2"
        data-testid={isLoading ? DataTestIdEnum.SEARCH_FILTERS_FOOTER_LOADING : undefined}
      >
        {isLoading
          ? (
              <>
                <Skeleton className="h-5 w-28 rounded-md" />
                <Skeleton className="h-12 flex-1 rounded-lg" />
              </>
            )
          : (
              <>
                <Button
                  variant="text"
                  color="primary"
                  className="w-auto whitespace-nowrap px-4 text-sm"
                  onClick={onClear}
                  data-testid={DataTestIdEnum.SEARCH_FILTERS_CLEAR}
                >
                  Limpar filtros
                </Button>
                <Button
                  variant="default"
                  color="primary"
                  className="flex-1"
                  disabled={applyButton.disabled}
                  onClick={applyButton.onClick}
                  data-testid={DataTestIdEnum.SEARCH_FILTERS_APPLY}
                >
                  {applyButton.label}
                </Button>
              </>
            )}
      </div>
    </div>
  );
}
